/**
 * useCrmWorkspacesStats - Hook para buscar workspaces com estatísticas
 * 
 * Utiliza RPC para agregação no banco, contornando limite de 1000 registros.
 * Segue padrão enterprise para alto volume de dados.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para rastreamento
const logger = createLogger('useCrmWorkspacesStats');

/**
 * Interface do Workspace com estatísticas calculadas via RPC
 */
export interface CrmWorkspaceWithStats {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  pipelines_count: number;
  deals_count: number;
  total_value: number;
  // Campos da categoria (adicionados via JOIN)
  category_id: string;
  category_name: string;
  category_slug: string;
  category_icon: string;
  // Campos de distribuição automática (v1.1.0)
  auto_distribution_enabled: boolean;
  default_queue_id: string | null;
  // Toggle de permissões granulares por fase (v1.2.0)
  phase_permissions_enabled: boolean;
  // Toggle de Modo Playbook por workspace (v1.3.0)
  playbook_mode_enabled: boolean;
}

/**
 * Hook para buscar workspaces CRM com estatísticas via RPC
 * Arquitetura enterprise que suporta alto volume de dados
 * 
 * @returns Objeto com dados, estado de loading e erro
 */
export function useCrmWorkspacesStats() {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['crm-workspaces-stats', currentTenant?.id],
    queryFn: async (): Promise<CrmWorkspaceWithStats[]> => {
      if (!currentTenant?.id) return [];

      logger.info('Buscando workspaces com estatísticas via RPC');

      // Chama RPC que faz agregação server-side (sem limite de 1000)
      const { data, error } = await supabase.rpc(
        'get_crm_workspaces_with_stats',
        { p_tenant_hint: currentTenant.id }
      );

      if (error) {
        logger.error('Erro ao buscar workspaces', error);
        throw error;
      }

      logger.info('Workspaces carregados com sucesso', { count: data?.length || 0 });

      // Mapeia resposta para tipagem correta
      return (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        description: row.description,
        icon: row.icon,
        color: row.color || '#3B82F6',
        is_active: row.is_active,
        sort_order: row.sort_order,
        created_at: row.created_at,
        pipelines_count: Number(row.pipelines_count) || 0,
        deals_count: Number(row.deals_count) || 0,
        total_value: Number(row.total_value) || 0,
        // Campos da categoria
        category_id: row.category_id,
        category_name: row.category_name || 'Geral',
        category_slug: row.category_slug || 'geral',
        category_icon: row.category_icon || 'icofont-briefcase',
        // Campos de distribuição automática (v1.1.0)
        auto_distribution_enabled: row.auto_distribution_enabled || false,
        default_queue_id: row.default_queue_id || null,
        // Toggle de permissões granulares por fase (v1.2.0)
        phase_permissions_enabled: row.phase_permissions_enabled ?? false,
        // Modo Playbook por workspace (v1.3.0)
        playbook_mode_enabled: row.playbook_mode_enabled ?? false
      }));
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });
}
